import { z } from 'zod';

export const managedSecretsProviderEnum = z.enum(['ssm-parameter', 'environment-variable']);
export type ManagedSecretsProvider = z.infer<typeof managedSecretsProviderEnum>;

export const managedSecretsTableItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  provider: managedSecretsProviderEnum,
  providerKey: z.string(),
  createdOn: z.coerce.date(),
  createdBy: z.object({
    id: z.number(),
    name: z.string(),
  }),
});

export const managedSecretSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  provider: managedSecretsProviderEnum,
  providerKey: z.string(),
});
export type ManagedSecret = z.infer<typeof managedSecretSchema>;

export const secretProviderSchema = z.enum(['secrets-manager']);
export type SecretsProviderType = z.infer<typeof secretProviderSchema>;

const SSM_SEGMENT_PATTERN = /^[a-zA-Z0-9._-]+$/;
const SSM_RESERVED_PREFIXES = ['aws', 'ssm'];

/** Collapses empty path segments (`/Foo//bar/` -> `/Foo/bar`) while keeping case and any leading slash. */
export function normalizeSsmParameterName(key: string): string {
  const trimmed = key.trim();
  const path = trimmed
    .split('/')
    .filter((segment) => segment.length > 0)
    .join('/');

  return trimmed.startsWith('/') ? `/${path}` : path;
}

export function isValidSsmParameterName(key: string): boolean {
  const segments = normalizeSsmParameterName(key).split('/').filter(Boolean);

  if (segments.length === 0) {
    return false;
  }

  const [first] = segments;

  return (
    segments.every((segment) => SSM_SEGMENT_PATTERN.test(segment)) &&
    !SSM_RESERVED_PREFIXES.some((prefix) => first.toLowerCase().startsWith(prefix))
  );
}

export const SSM_PARAMETER_NAME_ERROR =
  'Parameter must be slash-separated segments of letters, numbers and the symbols . - _ , and cannot start with "aws" or "ssm"';
